(() => {
  // TXOME: Mobile PDP sticky dual CTA (Add to cart / Choose Rx Lenses).
  // - Enabled only when the LensAdvizor CTA exists in the DOM.
  // - Shows only after the original CTA area has scrolled above the viewport.
  // - Mirrors Add to cart state (disabled/loading/text) by observing the original submit button.
  if (window.__txomePdpMobileStickyCtaInitialized) return;
  window.__txomePdpMobileStickyCtaInitialized = true;

  const mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };

  const SELECTORS = {
    sticky: '[data-txome-pdp-sticky-cta]',
    stickyAdd: '[data-txome-pdp-sticky-add]',
    stickyLens: '[data-txome-pdp-sticky-lens]',
    originalAdd: 'product-form [type="submit"][name="add"], product-form [type="submit"]',
    originalLens: '[data-lensadvizor-btn]',
  };

  const setSpacePx = (px) => {
    const safePx = Number.isFinite(px) && px > 0 ? `${Math.ceil(px)}px` : '0px';
    document.documentElement.style.setProperty('--txome-pdp-sticky-cta-space', safePx);
  };

  const getSticky = () => document.querySelector(SELECTORS.sticky);

  const getProductInfoScope = () =>
    document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('product-info') || document;

  const findOriginalAddBtn = () => getProductInfoScope().querySelector(SELECTORS.originalAdd);
  const findOriginalLensBtn = () => getProductInfoScope().querySelector(SELECTORS.originalLens);

  const state = {
    sticky: null,
    stickyAddBtn: null,
    stickyLensBtn: null,
    stickyAddLabel: null,
    stickyAddSpinner: null,

    originalAddBtn: null,
    originalLensBtn: null,

    addObserver: null,
    rafId: null,
  };

  const mirrorAddToCartState = () => {
    const { stickyAddBtn, stickyAddLabel, stickyAddSpinner, originalAddBtn } = state;
    if (!stickyAddBtn || !originalAddBtn) return;

    // disabled (sold out/unavailable)
    const disabled = originalAddBtn.hasAttribute('disabled');
    stickyAddBtn.toggleAttribute('disabled', disabled);

    // aria-disabled + loading (in-flight add-to-cart)
    const ariaDisabled = originalAddBtn.getAttribute('aria-disabled') === 'true';
    if (ariaDisabled) stickyAddBtn.setAttribute('aria-disabled', 'true');
    else stickyAddBtn.removeAttribute('aria-disabled');

    const loading = originalAddBtn.classList.contains('loading');
    stickyAddBtn.classList.toggle('loading', loading);
    if (stickyAddSpinner) stickyAddSpinner.classList.toggle('hidden', !loading);

    // label mirrors the original button's visible label (<span>...)</span>
    const span = originalAddBtn.querySelector('span');
    const text = (span ? span.textContent : originalAddBtn.textContent) || '';
    const trimmed = text.trim();
    if (stickyAddLabel && trimmed) stickyAddLabel.textContent = trimmed;
  };

  const bindOriginalAddObserver = () => {
    if (state.addObserver) state.addObserver.disconnect();
    state.addObserver = null;

    if (!state.originalAddBtn) return;

    state.addObserver = new MutationObserver(() => mirrorAddToCartState());
    state.addObserver.observe(state.originalAddBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const applyLensButtonStyles = () => {
    const { stickyLensBtn, originalLensBtn } = state;
    if (!stickyLensBtn || !originalLensBtn || !window.getComputedStyle) return;

    try {
      const cs = window.getComputedStyle(originalLensBtn);
      const isTransparent = (value) => {
        const v = String(value || '').trim().toLowerCase();
        return !v || v === 'transparent' || v === 'rgba(0, 0, 0, 0)' || v === 'rgba(0,0,0,0)';
      };

      if (cs && !isTransparent(cs.backgroundColor)) stickyLensBtn.style.backgroundColor = cs.backgroundColor;
      else stickyLensBtn.style.backgroundColor = '';

      if (cs && cs.color && !isTransparent(cs.color)) stickyLensBtn.style.color = cs.color;
      else stickyLensBtn.style.color = '';
    } catch (e) {
      // ignore
    }
  };

  const refreshOriginalRefs = () => {
    const nextAdd = findOriginalAddBtn();
    const nextLens = findOriginalLensBtn();

    const addChanged = nextAdd !== state.originalAddBtn;
    const lensChanged = nextLens !== state.originalLensBtn;

    state.originalAddBtn = nextAdd;
    state.originalLensBtn = nextLens;

    if (addChanged) {
      bindOriginalAddObserver();
      mirrorAddToCartState();
    }

    if (lensChanged) {
      applyLensButtonStyles();
    }

    // Enable condition: LensAdvizor CTA exists.
    if (state.sticky) state.sticky.hidden = !state.originalLensBtn;

    return Boolean(state.originalLensBtn && state.originalAddBtn);
  };

  const computeShouldShowSticky = () => {
    if (!state.originalAddBtn || !state.originalLensBtn) return false;

    const addRect = state.originalAddBtn.getBoundingClientRect();
    const lensRect = state.originalLensBtn.getBoundingClientRect();
    const bottom = Math.max(addRect.bottom, lensRect.bottom);

    // Show only after the original CTA block has scrolled fully above the viewport.
    return bottom < 0;
  };

  const setStickyVisible = (visible) => {
    if (!state.sticky) return;

    const shouldShow = Boolean(visible) && mq.matches && !state.sticky.hidden;
    state.sticky.classList.toggle('is-visible', shouldShow);
    setSpacePx(shouldShow ? state.sticky.getBoundingClientRect().height : 0);
  };

  const update = () => {
    state.rafId = null;

    if (!state.sticky) return;
    refreshOriginalRefs();

    if (!mq.matches || state.sticky.hidden) {
      setStickyVisible(false);
      return;
    }

    setStickyVisible(computeShouldShowSticky());
  };

  const scheduleUpdate = () => {
    if (state.rafId) return;
    state.rafId = requestAnimationFrame(update);
  };

  const bindStickyClicks = () => {
    const { stickyAddBtn, stickyLensBtn } = state;
    if (stickyAddBtn && stickyAddBtn.dataset.txomeBound !== 'true') {
      stickyAddBtn.dataset.txomeBound = 'true';
      stickyAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (stickyAddBtn.hasAttribute('disabled')) return;
        if (stickyAddBtn.getAttribute('aria-disabled') === 'true') return;
        const target = state.originalAddBtn || findOriginalAddBtn();
        if (target) target.click();
      });
    }

    if (stickyLensBtn && stickyLensBtn.dataset.txomeBound !== 'true') {
      stickyLensBtn.dataset.txomeBound = 'true';
      stickyLensBtn.addEventListener('click', (e) => {
        e.preventDefault();

        const tryClick = () => {
          const btn = state.originalLensBtn || findOriginalLensBtn();
          if (!btn) return false;
          btn.click();
          return true;
        };

        if (tryClick()) return;

        // Short retry (LensAdvizor button may be injected late).
        let tries = 0;
        const retry = () => {
          if (tryClick()) return;
          if (tries++ >= 8) {
            // Fallback: scroll back to the original CTA area.
            const target = findOriginalAddBtn() || findOriginalLensBtn() || getProductInfoScope();
            if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          window.setTimeout(retry, 200);
        };
        retry();
      });
    }
  };

  const mount = () => {
    state.sticky = getSticky();
    if (!state.sticky) return false;

    state.stickyAddBtn = state.sticky.querySelector(SELECTORS.stickyAdd);
    state.stickyLensBtn = state.sticky.querySelector(SELECTORS.stickyLens);
    state.stickyAddLabel = state.sticky.querySelector('.txome-pdp-sticky-cta__add-label');
    state.stickyAddSpinner = state.stickyAddBtn ? state.stickyAddBtn.querySelector('.loading__spinner') : null;

    bindStickyClicks();
    refreshOriginalRefs();
    mirrorAddToCartState();
    applyLensButtonStyles();

    // Ensure we don't keep any stale space if the bar is disabled/hidden.
    setStickyVisible(false);

    return true;
  };

  const init = () => {
    if (!mount()) return;

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const onMqChange = () => scheduleUpdate();
    if (mq.addEventListener) mq.addEventListener('change', onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange);

    // The LensAdvizor button can be injected asynchronously; retry briefly.
    let tries = 0;
    const tryRefresh = () => {
      if (refreshOriginalRefs()) {
        scheduleUpdate();
        return;
      }
      if (tries++ >= 40) return;
      window.setTimeout(tryRefresh, 250);
    };
    tryRefresh();

    // Rebind on variant changes (some apps re-render their buttons).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        refreshOriginalRefs();
        mirrorAddToCartState();
        applyLensButtonStyles();
        scheduleUpdate();
      });
    }

    // Initial checks (plus a delayed check for late-injected CTAs).
    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 1200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
